import { useEffect, useRef, useState } from 'react'

import { Input } from '@/components/ui/input'

import {
  type AddressPlaceDetails,
  useAddressAutocomplete
} from './use-address-autocomplete'

export type { AddressPlaceDetails } from './use-address-autocomplete'

export function AddressInput({
  id,
  value,
  onChange,
  onSelect,
  onInvalidate,
  placeholder,
  error
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  onSelect?: (details: AddressPlaceDetails) => void
  /** typing invalidates any geo previously resolved from a picked suggestion. */
  onInvalidate?: () => void
  placeholder?: string
  error?: string
}) {
  const { predictions, search, clear, rotateSession, getDetails } =
    useAddressAutocomplete()
  const [isOpen, setIsOpen] = useState(false)
  const [lookupFailed, setLookupFailed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const justSelectedRef = useRef(false)

  const initialValueRef = useRef(value)

  useEffect(() => {
    if (value === initialValueRef.current) return
    if (justSelectedRef.current) {
      justSelectedRef.current = false
      return
    }
    search(value)
  }, [value, search])

  useEffect(() => {
    setIsOpen(predictions.length > 0)
  }, [predictions])

  useEffect(() => {
    if (!isOpen) return
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  async function handleSelect(placeId: string, description: string) {
    justSelectedRef.current = true
    setIsOpen(false)
    clear()
    onChange(description)

    if (onSelect) {
      const details = await getDetails(placeId, description)
      rotateSession()
      setLookupFailed(details.lookupFailed)
      onSelect(details)
    } else {
      rotateSession()
    }
  }

  function handleTyping(next: string) {
    setLookupFailed(false)
    onInvalidate?.()
    onChange(next)
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      setIsOpen(false)
      clear()
    }
  }

  return (
    <div ref={containerRef} className='relative'>
      <Input
        id={id}
        type='text'
        value={value}
        onChange={event => handleTyping(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        /* not 'off': chrome ignores it on address fields. 'new-password'
           suppresses the native dropdown over ours without killing autofill
           on the rest of the form. */
        autoComplete='new-password'
        aria-invalid={Boolean(error) || lookupFailed || undefined}
      />
      {(lookupFailed || error) && (
        <p role='alert' className='mt-1 text-[12px] text-red-600'>
          {lookupFailed
            ? 'We couldn’t reach our address service. Please select your address again.'
            : error}
        </p>
      )}
      {isOpen && (
        <div className='absolute z-50 mt-1 max-h-[50svh] w-full divide-y divide-border overflow-y-auto rounded-xl border border-border bg-page shadow-sm'>
          {predictions.map(p => (
            <button
              key={p.placeId}
              type='button'
              onClick={() => void handleSelect(p.placeId, p.description)}
              className='w-full cursor-pointer px-3 py-3 text-left text-[15px] text-ink transition-colors hover:bg-cream/60 sm:py-2 sm:text-[13px]'
            >
              {p.description}
            </button>
          ))}
          <div className='flex items-center gap-1.5 border-t border-border px-3 py-1.5 text-[11px] text-ink-muted'>
            <svg className='size-3' viewBox='0 0 24 24'>
              <path
                d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z'
                fill='#4285F4'
              />
              <path
                d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                fill='#34A853'
              />
              <path
                d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                fill='#FBBC05'
              />
              <path
                d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                fill='#EA4335'
              />
            </svg>
            Google Maps
          </div>
        </div>
      )}
    </div>
  )
}
